import React, { useCallback, useRef, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import ActionSection from "features/terminal-details/common/actionSection";
import { toast } from "react-toastify";
import EquipmentFormFields from "./section/equipmentFormDetail";
import "./equipmentAddEditDetails.scss";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import { Card } from "ui-components/card/card";
import { getEquipmentInfoValidation, isEquipmentTypeWithoutVehicleIds, buildEquipmentSubmitPayload } from "../../../app/data/equipment/models";
import { useCreateEquipmentWithDocuments, useUpdateEquipment } from "hooks/useEquipment";
import UserState from "slices/user/UserState";
import { useSelector } from "react-redux";
import { userSelector } from "slices/user/userSlice";
import { ReactComponent as AlertIcon } from "images/alert.svg";
import ConfirmationModal from "ui-components/confirmation-modal/confirmationModal";

type DocumentFileType = 'REGISTRATION' | 'DOT_INSPECTION';

interface EquipmentInfo {
    id: string;
    unitNumber: string;
    equipmentType: string;
    assetDescriptor: string;
    make: string;
    model: string;
    year: string;
    vin: string;
    licensePlate: string;
    registrationDate: string;
    dotInspectionDate: string;
    location: string;
    status: string;
    leaseType: string;
    vendor: string;
    productLine: string;
    notes?: string;
    utilizationPercent: number;
    registrationDocUrl?: string;
    dotCertificateUrl?: string;
    assetTag: string;
    employeeAssigned: string;
    assignedDate: string;
    purchaseDate: string;
    lastCheckIn: string;
}

const EquipmentAddEditDetails = ({
    onClose,
    initialEquipmentData,
    show,
    onTeamCreated
}: {
    onClose: () => void;
    initialEquipmentData?: EquipmentInfo;
    show: boolean;
    onTeamCreated?: () => void;
}): React.ReactElement => {

    const userState: UserState = useSelector(userSelector);
    const formRef = useRef<FormikProps<EquipmentInfo>>(null);

    const initialValues: EquipmentInfo = initialEquipmentData || {
        id: "(Auto-generated)",
        unitNumber: "",
        equipmentType: "",
        assetDescriptor: "",
        make: "",
        model: "",
        year: "",
        vin: "",
        licensePlate: "",
        registrationDate: "",
        dotInspectionDate: "",
        location: userState.activeTerminal?.name || "",
        status: "",
        utilizationPercent: 0,
        leaseType: "",
        vendor: "",
        productLine: "",
        notes: "",
        registrationDocUrl: "",
        dotCertificateUrl: "",
        assetTag: "",
        employeeAssigned: "",
        assignedDate: "",
        purchaseDate: "",
        lastCheckIn: "",
    };

    const [filesToUpload, setFilesToUpload] = useState<{ [key in DocumentFileType]?: File }>({});
    const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

    const [resetKey, setResetKey] = useState(0);
    const { mutate: createEquipmentAndUpload, isLoading: isCreating } = useCreateEquipmentWithDocuments();
    const { mutateAsync: updateEquipment, isLoading: isUpdating } = useUpdateEquipment();

    const isEdit = !!initialEquipmentData && initialEquipmentData.id !== "(Auto-generated)";

    const handleSaveFileState = useCallback((payload: any, files: { [key in DocumentFileType]?: File }) => {
        setFilesToUpload(files);
    }, []);

    const handleCloseSidebar = () => {
        if (formRef.current?.dirty && !formRef.current?.isSubmitting) {
            setShowUnsavedChangesModal(true);
        } else {
            formRef.current?.resetForm();
            setFilesToUpload({});
            setResetKey(prev => prev + 1);
            onClose();
        }
    };

    const handleSubmit = (values: EquipmentInfo) => {
        if (!isEdit && !userState.activeTerminal?.name) {
            toast.error("Unable to save equipment. Select a terminal first.");
            return;
        }

        const cleanPayload = buildEquipmentSubmitPayload(values, {
            isEdit,
            location: userState.activeTerminal?.name,
        });
        const isPortableAsset = isEquipmentTypeWithoutVehicleIds(values.equipmentType);

        if (isEdit) {
            const equipmentId = cleanPayload.id;

            if (!equipmentId || equipmentId === "(Auto-generated)") {
                toast.error("Unable to update equipment. Missing equipment ID.");
                return;
            }

            updateEquipment({
                id: equipmentId,
                payload: cleanPayload
            }, {
                onSuccess: () => {
                    toast.success("Equipment updated.");
                    formRef.current?.resetForm();
                    setFilesToUpload({});
                    onTeamCreated && onTeamCreated();
                    setResetKey(prev => prev + 1);
                    onClose();
                },
                onError: (error: any) => {
                    const errorMessage = error?.response?.data?.message || error?.message || "Unable to update equipment.";
                    toast.error(errorMessage);
                }
            });

        } else {
            const files: { fileType: DocumentFileType; file: File; }[] = [];
            if (filesToUpload.REGISTRATION) {
                files.push({ fileType: 'REGISTRATION', file: filesToUpload.REGISTRATION });
            }
            if (!isPortableAsset && filesToUpload.DOT_INSPECTION) {
                files.push({ fileType: 'DOT_INSPECTION', file: filesToUpload.DOT_INSPECTION });
            }

            createEquipmentAndUpload({
                payload: cleanPayload,
                filesToUpload: files
            }, {
                onSuccess: () => {
                    toast.success("Equipment created.");
                    formRef.current?.resetForm();
                    setFilesToUpload({});
                    onTeamCreated && onTeamCreated();
                    setResetKey(prev => prev + 1);
                    onClose();
                },
                onError: (error: any) => {
                    const errorMessage = error?.response?.data?.message || error?.message || "Unable to create equipment.";
                    toast.error(errorMessage);
                }
            });
        }
    };

    const mainHeaderTitle = isEdit
        ? initialEquipmentData?.unitNumber ? `Unit Number ${initialEquipmentData.unitNumber}` : "Edit Equipment"
        : "Add New Equipment";

    const EditActionSection = (formikProps: FormikProps<EquipmentInfo>) => (
        <div className="actions">
            <ActionSection
                handleClear={handleCloseSidebar}
                handleSave={formikProps.handleSubmit}
                spinner={isUpdating}
                disabled={!formikProps.dirty || !formikProps.isValid}
                primaryButtonText="Save Changes"
                secondaryButtonText="Cancel"
            />
        </div>
    );

    const AddActionSection = (formikProps: FormikProps<EquipmentInfo>) => (
        <div className="equipment-add-footer">
            <ActionSection
                handleClear={handleCloseSidebar}
                handleSave={formikProps.handleSubmit}
                spinner={isCreating}
                disabled={!formikProps.dirty || !formikProps.isValid}
                primaryButtonText="Save Equipment"
                secondaryButtonText="Cancel"
            />
        </div>
    );

    return (
        <>
            <SlideOutSidebar
                show={show}
                header={mainHeaderTitle}
                onClose={handleCloseSidebar}
                size="small"
            >
                <Formik
                    key={resetKey}
                    onSubmit={handleSubmit}
                    initialValues={initialValues}
                    validationSchema={getEquipmentInfoValidation()}
                    innerRef={formRef}
                    enableReinitialize={true}
                >
                    {(formikProps: FormikProps<EquipmentInfo>) => (
                        <Form>
                            {isEdit && (
                                <div className="equipment-view-container">
                                    <div className="profile-header-equipment__title">
                                        <span className="profile-header-equipment__title-text">{"Equipment Information"}</span>
                                        {EditActionSection(formikProps)}
                                    </div>
                                </div>
                            )}
                            <div className="equipment-view-container">
                                <EquipmentFormFields
                                    formikProps={formikProps}
                                    isEdit={isEdit}
                                    CardComponent={Card}
                                    initialData={initialEquipmentData}
                                    handleSave={handleSaveFileState}
                                />
                            </div>
                            {!isEdit && AddActionSection(formikProps)}
                        </Form>
                    )}
                </Formik >
            </SlideOutSidebar >
            <ConfirmationModal
                opened={showUnsavedChangesModal}
                onCancel={() => setShowUnsavedChangesModal(false)}
                onConfirm={() => {
                    formRef.current?.resetForm();
                    setFilesToUpload({});
                    setResetKey(prev => prev + 1);
                    setShowUnsavedChangesModal(false);
                    onClose();
                }}
                confirmButtonText="Discard Changes"
                cancelButtonText="Keep Editing"
            >
                <div className="confirmation-modal__content">
                    <AlertIcon />
                    <div>
                        You have unsaved changes.<br />
                        Are you sure you want to discard them and close the form?
                    </div>
                </div>
            </ConfirmationModal>
        </>
    );
};

export default EquipmentAddEditDetails;
